/**
 * BottomNav — Braydin Wright fan-app bottom tab bar.
 * Fixed to the bottom, centered to the app column. Olive active state.
 * Tabs: Home (/welcome), Explore (/gallery), Updates (/newsroom), Account (/account).
 */
import { useNavigate, useLocation } from 'react-router-dom'

type Tab = {
  id: string
  route: string
  label: string
  match: (path: string) => boolean
  icon: (active: boolean) => JSX.Element
}

const stroke = (active: boolean) => (active ? '#6B7850' : '#21231C')

const tabs: Tab[] = [
  {
    id: 'home',
    route: '/welcome',
    label: 'Home',
    match: (p) => p === '/welcome' || p === '/',
    icon: (a) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={stroke(a)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      </svg>
    ),
  },
  {
    id: 'explore',
    route: '/gallery',
    label: 'Explore',
    match: (p) => p.startsWith('/gallery'),
    icon: (a) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={stroke(a)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
      </svg>
    ),
  },
  {
    id: 'updates',
    route: '/newsroom',
    label: 'Updates',
    match: (p) => p.startsWith('/newsroom'),
    icon: (a) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={stroke(a)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
    ),
  },
  {
    id: 'account',
    route: '/account',
    label: 'Account',
    match: (p) => p.startsWith('/account'),
    icon: (a) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={stroke(a)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
      </svg>
    ),
  },
]

export function BottomNav() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (
    <nav
      className="fixed inset-x-0 z-40 flex justify-center pointer-events-none"
      style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
    >
      {/* floating rounded pill */}
      <div className="pointer-events-auto flex items-center gap-1 px-3 py-2 rounded-full bg-braydin-cream/95 backdrop-blur-md border border-braydin-taupe/50 shadow-[0_8px_24px_rgba(43,43,38,0.18)]">
        {tabs.map((t) => {
          const active = t.match(pathname)
          return (
            <button
              key={t.id}
              onClick={() => navigate(t.route)}
              aria-label={t.label}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center justify-center w-12 h-12 rounded-full transition-all active:scale-90 ${active ? 'bg-braydin-olive/15' : 'hover:bg-braydin-sand'}`}
            >
              {t.icon(active)}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
